import * as THREE from 'three';
import { renderComposited } from '../scene/blend-composite.js';
import { calibGridMat, updateCalibGrid } from '../scene/calib-grid.js';
import { activeCam, cornerRenderable, isSiteLike, updateFlatCam, updateOrbit, updateScreenCam } from '../scene/cameras.js';
import { TONE, cornerCam, flatCam, helpers, orbitCam, renderer, scene } from '../scene/context.js';
import { panelCorners, rightPanelDesc } from '../scene/panels.js';
import { faceArrow, faceTag, gizmo, gizmoLayerVisible, gizmoScene, setLightViz, updateFaceArrow, updateGizmo, updateLightVizSelection } from '../scene/gizmo.js';
import { _lbC, _lbS, _lbSc, _lbT, eyeCamMat, eyeVec, localBounds, selBox, selBoxMat, setBezelPass, updateEyeHelpers } from '../scene/helpers.js';
import { fitAllShadows } from '../scene/lighting.js';
import { getSel, setCameraObjViz, setNullMarkers } from '../scene/objects.js';
import { consumeRender, emit, requestRender } from '../state/bus.js';
import { $, S, clamp } from '../state/store.js';
import { syncCamRig } from '../ui/camrig.js';
import { mask, paintMask } from '../ui/mask-editor.js';
import { OVERSCAN_MAX, blitInto, blitSite, blitSiteCorner, blitSiteGizmo, blitSiteGizmoCorner, blitSplitFlat, hideSiteGizmo } from '../ui/site-view.js';

/* The gizmo, the selection outline and the face tag are all drawn through
   the SAME frustum the pointer is mapped through, so what you see is what
   you can grab.

   They used to render through a much wider one (4x in Flat, 1.3x On-site),
   so that a handle on an object parked outside the calibrated screen
   rectangle would still be drawn on canvas. The gizmo was rescaled to
   compensate, which kept its SIZE right — but a widened frustum also
   reprojects every off-centre point toward the middle, and nothing
   compensated for that. The gizmo was therefore drawn progressively further
   from its object the further that object sat from the centre (measured:
   ~106px off at 1m up, ~244px at 2.3m), while picking still raycast through
   the normal frustum — so it was both visibly detached and unclickable.

   The reach it bought was an illusion too: an off-frame gizmo was drawn back
   inside the canvas, but its true position is outside the pick frustum by
   definition, so clicking it never hit anything. Removing the widening loses
   nothing that worked and fixes both symptoms. An object parked off-frame
   now simply has no on-canvas gizmo — move it back from Orbit or the
   inspector's numeric fields. */

/* Every fat-line material (scene/helpers.js's selBoxMat/eyeCamMat, plus each
   Camera/Light object's own body material) draws at a constant SCREEN pixel
   width, which only comes out right if its resolution uniform matches
   whatever size the renderer is currently set to — renderer.setSize runs
   several times a frame in drawSplit (a different size per pane), so this
   re-reads it fresh right before every render pass rather than trying to
   keep it in sync at each individual setSize call site. Camera/Light bodies
   render in the MAIN scene pass (they're normal content, not gizmoScene
   overlays), so this runs before those too, not just the gizmo passes. */
const _selRes = new THREE.Vector2();
function syncFatLineRes(){
  renderer.getSize(_selRes);
  selBoxMat.resolution.copy(_selRes);
  eyeCamMat.resolution.copy(_selRes);
  calibGridMat.resolution.copy(_selRes);
  for(const o of S.objects){
    if(o.type!=='camera' && o.type!=='light' && o.type!=='null') continue;
    const mats = o.node && o.node.userData.mats;
    if(!mats) continue;
    for(const m of mats) if(m.isLineMaterial) m.resolution.copy(_selRes);
  }
}

// Orbit's clear colour — kept in sync BY HAND with app.css's --stage in
// each theme, since a WebGL clear colour has no way to read a CSS custom
// property. If --stage's values change, these have to change with them.
/* STAGE_DARK is app.css's --stage: Blender's viewport lightness with this
   app's cool tint. A WebGL clear cannot read a CSS variable, so the two are
   kept equal by hand — change one, change the other. */
const STAGE_DARK = 0x36393D, STAGE_LIGHT = 0xE3E4E7;

/* ═══════════════════════════════════════════════════════════
   RENDER
   ═══════════════════════════════════════════════════════════ */

/* Split's left pane (Orbit) fills its half exactly like standalone Orbit
   fills the whole stage — no aspect constraint, since composing needs the
   full box. The right pane (Flat preview) is letterboxed to the
   deliverable's own aspect within its half, exactly like standalone Flat,
   just confined to a smaller box. Both panes share one top/bottom gutter
   (same measurement the flat/site branch below uses) so the floating view
   tabs and hint line never sit over either one. */
function fitSplit(stage, W, H, fb){
  const stageR = stage.getBoundingClientRect();
  const clearTop = el => (!el || el.classList.contains('hidden'))
    ? 0 : el.getBoundingClientRect().bottom - stageR.top;
  /* The palette floats over the left pane here too, and Split deliberately
     does NOT shift its camera clear of it the way standalone Orbit does (see
     frameOrbitClearOfPalette) — so the only thing keeping the two apart is
     this gutter. Without it the left pane slides under the toolbar on a tall
     window and whatever sits there is visible through the glass and
     unclickable. */
  const clearLeft = el => (!el || el.classList.contains('hidden'))
    ? 0 : el.getBoundingClientRect().right - stageR.left;
  const gap = 14;
  const topH = Math.max(clearTop($('#viewtabs')), clearTop($('#hint'))) + gap;
  const leftH = clearLeft($('#palette')) + gap;
  const rowH = Math.max(40, H - topH - gap);
  /* One pixel, and it is the grey line (app.css): the two halves meet at it
     rather than being held apart by a margin of empty stage either side. */
  const paneGap = 1;
  const rowW = Math.max(80, W - leftH - gap);
  const halfW = (rowW - paneGap) / 2;

  const row = $('#splitRow');
  row.style.width = Math.round(rowW)+'px';
  row.style.height = Math.round(rowH)+'px';
  row.style.gap = paneGap+'px';
  row.style.marginLeft = ((W - rowW) / 2 < leftH)
    ? Math.round(2*leftH - W + rowW)+'px' : '0px';
  // Same margin-vs-flex-centre reconciliation fitStage's flat/site branch
  // uses below, applied to #splitRow instead of #fitbox — see its comment.
  row.style.marginTop = Math.round(2*topH - H + rowH)+'px';


  fb.style.width = Math.max(40, Math.round(halfW))+'px';
  fb.style.height = Math.max(40, Math.round(rowH))+'px';
  fb.style.marginTop = '0px';

  const fbR = $('#fitboxSplitR');
  const ar = S.screen.w/S.screen.h;
  let w = halfW, h = w/ar;
  if(h > rowH){ h = rowH; w = h*ar; }
  fbR.style.width = Math.max(40, Math.round(w))+'px';
  fbR.style.height = Math.max(24, Math.round(h))+'px';
}

/* Keep the Orbit scene out from under the floating tool palette.

   Flat and On-site letterbox their render into a gutter so the floating
   panels never sit over it (see fitStage below). Orbit deliberately doesn't
   — composing wants the whole box — so the palette floats over live canvas.
   Its container is pointer-events:none, but its BUTTONS can't be: they're
   buttons. So anything that lands beneath one is visible through the glass
   and unclickable, which is what made the gizmo impossible to grab. It is
   worst on a tall/narrow window, where that fixed-width column is a much
   bigger share of the canvas and the scene sits further left to begin with.

   Rather than shrink the canvas (dead space, and the background would stop
   filling the stage), the CAMERA frames clear of it: the frustum is widened
   by the intruded width and only the part of it that isn't covered is
   rendered, so the scene shifts right into open canvas. Nothing scales —
   vertical fov is untouched, so this is a pure translation — and picking
   needs no special case because a raycast reads the same projection matrix
   the render does.

   aspect is set from the FULL virtual width, not the canvas width: the
   sub-region then narrows it back to the canvas's own ratio. Setting it
   from cw would stretch everything horizontally. */
function frameOrbitClearOfPalette(fb, cw, ch){
  const pal = $('#palette');
  let pad = 0;
  /* Standalone Orbit only. Split's left pane is already half width, so
     shifting its contents by a fixed ~135px column moves them a great deal
     more in relative terms — and that pane is a composing view people aim
     at directly, where content quietly sitting somewhere other than where
     the camera points is worse than the overlap it would be avoiding. */
  if(S.view === 'orbit' && pal && !pal.classList.contains('hidden')){
    const p = pal.getBoundingClientRect(), r = fb.getBoundingClientRect();
    // Only what actually intrudes past the canvas's left edge; negative
    // (palette entirely beside the render, as in Flat) means nothing to do.
    if(p.width && r.width) pad = clamp(Math.round(p.right - r.left + 12), 0, Math.round(cw * .4));
  }
  const h = Math.max(1, ch);
  if(pad <= 0){
    orbitCam.clearViewOffset();
    orbitCam.aspect = cw / h;
  }else{
    /* 2*pad, not pad. Widening the virtual image by N and keeping its LEFT
       cw moves the centre to (cw+N)/2 — a shift of only N/2. To clear an
       obstruction `pad` wide the scene has to move by the whole `pad`, so
       the virtual image needs to be 2*pad wider. */
    const fullW = cw + pad * 2;
    orbitCam.aspect = fullW / h;
    orbitCam.setViewOffset(fullW, h, 0, 0, cw, h);
  }
  orbitCam.updateProjectionMatrix();
}

/* A corner screen's two deliverables, side by side, sharing a height. Aspects
   come from PIXELS, not metres: each panel is delivered as its own file at its
   own resolution, and the two can legitimately differ in px-per-metre — the
   Calibrate panel warns about that separately, but the preview must show what
   will actually be delivered, not a corrected version of it.

   Same gutter measurement fitStage's own flat branch uses, so the floating
   toolbars sit clear of both panes rather than over the left one. */
function fitCorner(stage, W, H, fb){
  const stageR = stage.getBoundingClientRect();
  const clearTop = el => (!el || el.classList.contains('hidden'))
    ? 0 : el.getBoundingClientRect().bottom - stageR.top;
  const clearLeft = el => (!el || el.classList.contains('hidden'))
    ? 0 : el.getBoundingClientRect().right - stageR.left;
  const gap = 14;
  const topH = Math.max(clearTop($('#viewtabs')), clearTop($('#hint'))) + gap;
  const leftH = clearLeft($('#palette')) + gap;

  const arL = S.res.w / S.res.h, arR = Math.max(1, S.cornerPanel.resW) / S.res.h;
  const availW = Math.max(80, W - leftH - gap), availH = Math.max(40, H - topH - gap);
  /* Flush: the pair is one picture across the corner, so nothing sits between
     them — a divider would break exactly the join being judged. */
  let h = availH, w = h*(arL + arR);
  if(w > availW){ h = availW / (arL + arR); w = availW; }
  const bh = Math.max(24, Math.round(h));
  const bwL = Math.max(20, Math.round(h*arL)), bwR = Math.max(20, Math.round(h*arR));

  const fbR = $('#fitboxCornerR');
  fbR.classList.remove('hidden');
  fb.style.width = bwL+'px';  fb.style.height = bh+'px';
  fbR.style.width = bwR+'px'; fbR.style.height = bh+'px';
  /* The pair is centred as a group by .view's flex, so the same
     "align centres the MARGIN box" arithmetic the single pane uses applies —
     just against the pair's combined width. */
  const total = bwL + bwR;
  fb.style.marginLeft = ((W - total) / 2 < leftH)
    ? Math.round(2*leftH - W + total)+'px' : '0px';
  const mt = Math.round(2*topH - H + bh)+'px';
  fb.style.marginTop = mt; fbR.style.marginTop = mt;
}

export function fitStage(){
  const stage = $('#stage');
  const W = stage.clientWidth, H = stage.clientHeight;
  const fb = $('#fitbox');
  /* Cleared unconditionally: only the flat/on-site branch below ever sets a
     left gutter, and a stale one would survive a switch to Orbit or Split. */
  fb.style.marginLeft = '0px';
  /* Cleared unconditionally too: a corner pane left showing after switching
     view or turning corner mode off would sit there holding a stale render. */
  if(!(S.view === 'flat' && S.cornerPanel)) $('#fitboxCornerR').classList.add('hidden');
  /* Flat is the deliverable, so it is shown without the ring and drop shadow
     every other pane wears — see .fitbox--bare. */
  const bare = S.view === 'flat';
  fb.classList.toggle('fitbox--bare', bare);
  $('#fitboxCornerR').classList.toggle('fitbox--bare', bare);
  if(S.view === 'orbit'){
    fb.style.width = W+'px'; fb.style.height = H+'px'; fb.style.marginTop = '0px';
  }else if(S.view === 'split'){
    fitSplit(stage, W, H, fb);
  }else if(S.view === 'flat' && S.cornerPanel){
    fitCorner(stage, W, H, fb);
  }else{
    /* Flat and On-site are meant to preview the deliverable exactly, so the
       floating toolbars have to sit in a real gutter around the render,
       never on top of it. A flat 40px guess used to stand in for that
       gutter — plenty on a big monitor, where the panels are a small
       fraction of the stage, but nowhere near enough on a small screen,
       where the same fixed-size panels cover a much bigger share of a much
       smaller canvas. Measuring their actual on-screen extent (only the
       ones currently visible) keeps the reserved space honest at any
       screen size instead of guessing. */
    const stageR = stage.getBoundingClientRect();
    const clearTop = el => (!el || el.classList.contains('hidden'))
      ? 0 : el.getBoundingClientRect().bottom - stageR.top;
    const clearBottom = el => (!el || el.classList.contains('hidden'))
      ? 0 : stageR.bottom - el.getBoundingClientRect().top;
    // Both the view tabs (top-right) and the hint line (top-left, beside
    // the palette) float over the top edge now — nothing floats over the
    // bottom any more. The eye/screen/output readout that used to sit in a
    // HUD up here lives in Master Settings instead (ui/rail-right.js's
    // renderMasterSettings).
    /* The tool palette floats over the LEFT edge, and it was never measured —
       the render simply took W-60 and centred, so on a stage that is tall
       relative to the deliverable's aspect the render grew wide enough to
       slide underneath it. Everything still looked right, because the palette
       is glass: you could see the object through the toolbar and not select
       it, because the button on top took the click. Same gutter reasoning as
       the top edge above, applied to the axis it was missing. */
    const clearLeft = el => (!el || el.classList.contains('hidden'))
      ? 0 : el.getBoundingClientRect().right - stageR.left;
    const gap = 14;
    const topH = Math.max(clearTop($('#viewtabs')), clearTop($('#hint'))) + gap;
    const botH = gap;
    const leftH = clearLeft($('#palette')) + gap;
    const ar = S.screen.w/S.screen.h;
    let w = W - leftH - gap, h = w/ar;
    if(h > H-topH-botH){ h = H-topH-botH; w = h*ar; }
    const bh = Math.max(24,Math.round(h));
    const bw = Math.max(40,Math.round(w));
    fb.style.width = bw+'px';
    fb.style.height = bh+'px';
    /* Centred is right whenever it fits — a render shoved against the palette
       on a wide screen would be worse than the bug. Only when centring would
       put the left edge inside the gutter is it pushed clear, by the same
       "align centres the MARGIN box" arithmetic the top edge uses below. */
    fb.style.marginLeft = ((W - bw) / 2 < leftH)
      ? Math.round(2*leftH - W + bw)+'px' : '0px';
    // .view centres #fitbox via flexbox, which splits leftover space evenly —
    // fine when topH and botH match, wrong whenever they don't (the view
    // tabs pill is usually taller than the hint line below it).
    // align-items:center centres the item's margin box, so an explicit
    // margin-top of X moves the visible top edge to (H-bh)/2 + X/2 —
    // solving that for "exactly topH clear on top, any slack left below"
    // gives X = 2*topH - H + bh.
    fb.style.marginTop = Math.round(2*topH - H + bh)+'px';
  }
  const cw = fb.clientWidth, ch = fb.clientHeight;
  /* Strict 'site' on purpose: in Split this is sizing the LEFT pane, and the
     site pass sizes itself for the right one (see sizeRendererForSite). */
  if(S.view === 'site'){
    sizeRendererForSite();
  }else{
    // Supersampling is normally an export-only pass (see export/png.js) — too
    // expensive to redo every frame while dragging. previewQuality trades that
    // performance for accuracy: the live Orbit/Flat canvas renders at the same
    // multiple of its display size that the export would, so what you see
    // while working already looks like the deliverable, not just close to it.
    const ss = S.previewQuality ? clamp(Math.round(S.ss)||1, 1, 4) : 1;
    renderer.setPixelRatio(Math.min(devicePixelRatio,2) * ss);
    renderer.setSize(cw, ch, false);
  }
  frameOrbitClearOfPalette(fb, cw, ch);
  emit('stage:fit');   // #fitbox's on-screen size is final now — chrome.js repaints the flat watermark off it
  requestRender();
}

/* Orient every face-camera object toward the active camera. Done per frame rather
   than baked into the transform, because "face the camera" depends on which camera
   is rendering — the orbit camera while composing, the off-axis viewer eye in Flat
   and On-site. The object's own rotation is ignored while this is on. */
export function applyBillboards(cam){
  /* Face-the-camera is an ON-SITE behaviour: it makes a plane or text sit square
     to the viewer's eye so it reads flat on the wall. In Orbit you are inspecting
     the scene from arbitrary angles, so billboarding there would spin the object
     around under you and hide its real placement. So we only billboard in the
     deliverable views (on-site / flat); in Orbit the object keeps its own
     transform. A single Roll angle then spins the billboard in its own plane.

     Keyed off which camera is actually about to render, not S.view — Split
     renders both an orbit-style and a flat-style pass in the same frame
     (scene/renderer.js's drawSplit), so a single global "the current view is
     X" reading can't describe both at once the way it could when only one
     view was ever active. */
  const orbit = cam === orbitCam;
  const camPos = new THREE.Vector3();
  cam.getWorldPosition(camPos);
  const up = new THREE.Vector3(0,1,0);
  const m = new THREE.Matrix4();
  for(const ob of S.objects){
    if(!ob.faceCamera || ob.type==='light' || !ob.node || !ob.visible) continue;
    if(orbit){
      // show the object's own orientation while inspecting; roll still applies so
      // what you set is what you see when you switch back to On-site
      ob.node.rotation.set(0, 0, (ob.faceRoll||0)*Math.PI/180);
      continue;
    }
    m.lookAt(camPos, ob.node.position, up);
    ob.node.quaternion.setFromRotationMatrix(m);
    if(ob.faceRoll) ob.node.rotateZ(ob.faceRoll*Math.PI/180);
  }
}

/* Shared by draw() and drawSplit() — everything that's about the SCENE
   (helpers, shadows, tone mapping) rather than about which camera/canvas a
   particular pass renders through. Selection outline used to live here too,
   but it needs updateSelBox() below instead — see that function's comment. */
/* THE MOCKUP RENDERS AT THE DELIVERABLE'S OWN RESOLUTION, not at a DOM box:
   blitSite warps that canvas onto the photograph, so its pixels are the
   deliverable's and the on-screen size is the photo's. Its own function
   because two callers need it — fitStage for the standalone view, and the
   site pass itself in Split, where the left pane has just sized the renderer
   to something else entirely. */
function sizeRendererForSite(){
  const ar = S.screen.w / S.screen.h;
  const pw = Math.min(1800, Math.round(S.res.w)), phh = Math.round(pw / ar);
  renderer.setPixelRatio(1);
  renderer.setSize(pw, phh, false);
}

function prepDraw(){
  /* A lamp body is a handle for the object, the same as the gizmo and the
     selection outline — and those already draw in every view. Restricting the
     body to Orbit made lights invisible in On-site, which is where they get
     positioned. It draws wherever you can manipulate it; the export is the one
     place it must never appear, and exportPNG turns it off explicitly. */
  // the eye can move every frame now that it can be dragged, so the frustum and
  // eye marker refresh here rather than only on a full helper rebuild
  updateEyeHelpers();
  setLightViz(true);
  updateLightVizSelection();
  setCameraObjViz(true);

  fitAllShadows();
  renderer.toneMapping = TONE[S.tone] ?? TONE.none;
}

/* Split out of prepDraw() (which runs once per FRAME, before any camera-
   specific pass) because a face-camera object's matrixWorld isn't correct
   until AFTER applyBillboards(cam) has run for the camera about to render —
   applyBillboards sets .rotation but doesn't itself recompute matrixWorld,
   which normally only happens inside renderer.render()'s own traversal.
   Reading matrixWorld before that (as this used to, from inside prepDraw)
   meant the selection outline used whatever rotation was left over from the
   PREVIOUS render — and since rendering here is on-demand rather than
   continuous, a face-camera object's outline could stay visibly wrong
   (still showing its un-billboarded tilt) until something else happened to
   trigger another frame. updateMatrixWorld(true) forces the recompute right
   now, using the rotation applyBillboards just set, instead of waiting for
   the render call that's about to happen anyway. */
function updateSelBox(){
  selBox.visible = false;
  const sel = getSel();
  if(!sel || !sel.node || !sel.visible) return;
  sel.node.updateMatrixWorld(true);
  const lb = localBounds(sel.node);
  if(lb.isEmpty()) return;
  lb.getCenter(_lbC); lb.getSize(_lbS);
  _lbS.x = Math.max(_lbS.x, 1e-4);
  _lbS.y = Math.max(_lbS.y, 1e-4);
  _lbS.z = Math.max(_lbS.z, 1e-4);
  selBox.matrix
    .copy(sel.node.matrixWorld)
    .multiply(_lbT.makeTranslation(_lbC.x, _lbC.y, _lbC.z))
    .multiply(_lbSc.makeScale(_lbS.x, _lbS.y, _lbS.z));
  selBox.matrixWorldNeedsUpdate = true;
  selBox.visible = true;
}

/* Split (Orbit + Flat, side by side) only exists in the no-photo/manual
   path (see ui/chrome.js's setView), so it never has to coexist with
   On-site's homography/blit machinery — the flat pane here is a plain,
   unwarped snapshot copied into a second canvas, no CSS matrix3d involved.

   Two full passes through the one shared renderer/canvas rather than a
   second WebGLRenderer (duplicate GPU context, duplicate shadow maps) or a
   split viewport on one canvas (would need real hit-testing to keep the
   right pane un-clickable). The flat pass runs first and gets copied out
   immediately; the orbit pass runs last, so whatever it leaves in the
   canvas — the live, interactive view — is what's actually on screen. */
/* @param {boolean} siteRight  the right pane is the On-site mockup, drawn by
 *        draw()'s own site pass after this returns — so this draws the LEFT
 *        pane only and leaves the renderer for that pass to size itself. */
function drawSplit(siteRight){
  /* Both of this function's panes are Orbit or Flat, never the mockup — the
     site pane is drawn by draw() itself. So the margins wear their black
     bars here whatever the On-site setting says: there is no photograph
     behind these passes for a hole to reveal. */
  setBezelPass(false);
  applyScreenClip(false);           // same reason as drawCorner: draw() returns into us
  const ss = S.previewQuality ? clamp(Math.round(S.ss)||1, 1, 4) : 1;
  const pr = Math.min(devicePixelRatio,2) * ss;

  const fbR = $('#fitboxSplitR');
  const rw = siteRight ? 0 : fbR.clientWidth, rh = siteRight ? 0 : fbR.clientHeight;
  if(rw && rh){
    helpers.visible = false;
    renderer.setClearColor(new THREE.Color(S.bg.color), S.bg.transparent?0:1);
    updateFlatCam();
    applyBillboards(flatCam);
    updateSelBox();
    updateGizmo(flatCam);
    updateFaceArrow(flatCam);
    renderer.setPixelRatio(pr);
    renderer.setSize(rw, rh, false);
    syncFatLineRes();
    renderComposited(scene, flatCam);
    if(gizmoLayerVisible()){
      renderer.autoClear = false;
      renderer.clearDepth();
      renderer.render(gizmoScene, flatCam);
      renderer.autoClear = true;
    }
    blitSplitFlat();
  }

  const fbL = $('#fitbox');
  const cw = fbL.clientWidth, ch = fbL.clientHeight;
  helpers.visible = S.showHelpers;
  renderer.setClearColor(document.documentElement.dataset.theme === 'light' ? STAGE_LIGHT : STAGE_DARK, 1);
  updateOrbit();
  applyBillboards(orbitCam);
  updateSelBox();
  updateGizmo(orbitCam);
  updateFaceArrow(orbitCam);
  renderer.setPixelRatio(pr);
  renderer.setSize(cw, ch, false);
  syncFatLineRes();
  renderComposited(scene, orbitCam);
  if(gizmoLayerVisible()){
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(gizmoScene, orbitCam);
    renderer.autoClear = true;
  }
  /* Left to the site pass when it is about to run: the mask is edited on the
     mockup there, and painting it here would do it against the orbit pane. */
  if(mask.active && !siteRight) paintMask();
}

/* ── reaching past the screen, without anyone having to ask ──────────────────
   The On-site frustum IS the screen rectangle, so an object outside the
   screen's viewing cone has no on-canvas position and therefore no handle —
   and a key light belongs outside it, above and in front of the sign, which is
   where lights go. Rendering wider fixes that, but the amount needed is not a
   number anyone should have to find: it depends on the calibration and on what
   is being reached for. It is derived here instead.

   Recomputed only when the SELECTION changes, which is what keeps it steady:
   the view must not re-frame itself under a drag, and selId is constant for
   the whole of one. Moving something out of frame by typing coordinates will
   not widen the view until it is reselected — deliberate, since re-framing
   mid-keystroke would be worse than the wait.

   NDC scales as 1/margin, so the margin that brings a point to the frame edge
   is its own NDC distance; a quarter again puts it inside with room to grab
   rather than balanced on the boundary. */
let autoKey = '', autoOver = 1;
function autoOverscan(){
  const key = S.selId + '|' + S.view + '|' + S.solved + '|' + S.siteReach + '|' + !!S.photo;
  if(key === autoKey) return autoOver;
  autoKey = key;
  autoOver = 1;
  const sel = getSel();
  if(S.siteReach && isSiteLike() && S.solved && sel && sel.node){
    updateFlatCam(1);                       // measure against the real screen rect
    const p = sel.node.position.clone().project(flatCam);
    if(isFinite(p.x) && isFinite(p.y)){
      const outBy = Math.max(Math.abs(p.x), Math.abs(p.y));
      if(outBy > 1) autoOver = Math.min(OVERSCAN_MAX, Math.ceil(outBy * 1.25 * 10) / 10);
    }
  }
  S.siteOverscan = autoOver;
  return autoOver;
}

/* A recess and a frame are features OF the screen — an opening cut into it and
   a border around it. They only read correctly through the screen's own
   rectangle, and once the view reaches past that edge you can see the outside
   of the box instead: a slab hanging over the building, which is construction
   the frame is supposed to hide. Everything else out there (a light, a model,
   a text object) genuinely lives in the world and is the whole reason for
   reaching past the edge, so only these two are clipped.

   Four planes, each through the eye and one edge of the screen — exactly the
   viewing cone. The Plane objects are reused and rewritten in place: their
   VALUES can change every frame for free, while changing the length of a
   material's clippingPlanes array recompiles its shader, so that is done only
   when clipping switches on or off. */
const CONE = [new THREE.Plane(), new THREE.Plane(), new THREE.Plane(), new THREE.Plane()];
const _cA = new THREE.Vector3(), _cB = new THREE.Vector3(), _cO = new THREE.Vector3();
let clipOn = false;
/* The cone of ONE panel. A corner screen needs the union of two, which cannot
   be written as an intersection of planes — but it never has to be: the panels
   render in separate passes, so each pass carries its own panel's cone and the
   union is what the two passes produce together. Only the plane VALUES change
   between them, never the array's length, so switching costs nothing (a length
   change would recompile the shader). */
function updateConePlanes(desc = null){
  const e = eyeVec();
  const c = desc ? panelCorners(desc) : (() => {
    const w = S.screen.w/2, h = S.screen.h/2;
    return [[-w,h,0],[w,h,0],[w,-h,0],[-w,-h,0]].map(v => new THREE.Vector3(v[0],v[1],v[2]));
  })();
  const centre = desc ? desc.pos : _cO.set(0,0,0);
  for(let i=0;i<4;i++){
    _cA.copy(c[i]); _cB.copy(c[(i+1)%4]);
    CONE[i].setFromCoplanarPoints(_cA, _cB, e);
    // point it inward — the panel's own centre must be on the kept side
    if(CONE[i].distanceToPoint(centre) < 0) CONE[i].negate();
  }
}
function applyScreenClip(on, desc = null){
  if(on) updateConePlanes(desc);
  if(on === clipOn) return;                 // values already refreshed above

  clipOn = on;
  renderer.localClippingEnabled = true;
  for(const o of S.objects){
    if((o.type !== 'recess' && o.type !== 'frame') || !o.node) continue;
    o.node.traverse(n=>{
      const mats = Array.isArray(n.material) ? n.material : (n.material ? [n.material] : []);
      for(const m of mats){ m.clippingPlanes = on ? CONE : null; m.needsUpdate = true; }
    });
  }
}

/* A corner screen's two deliverables, in one frame. Same shape as drawSplit —
   two sequential passes on the one renderer, each with its own setSize and
   syncFatLineRes — and for the same reason: there is a single WebGL canvas, so
   the pane that is NOT rendered last has to be copied off it first.

   The right panel goes first and is blitted out, leaving the live canvas
   holding the left. That ordering is not arbitrary: #fitbox's canvas is the
   interactive one every other view already targets, and it should end the frame
   showing the panel that is selected by default. */
function drawCorner(){
  setBezelPass(false);                  // Flat: no photograph behind it either
  /* THE COMMON PATH SETS THIS AND THEN RETURNS INTO US, so we have to set it
     ourselves: draw() dispatches here before it reaches applyScreenClip, and a
     view that does not state the clip it needs inherits whatever the last one
     left. On-site turns the cone ON whenever the selection reaches past the
     screen edge — so selecting something over the second panel there and coming
     back to Flat left the recess clipped by a cone built for a widened On-site
     frustum, and most of the first panel simply vanished. It survived
     deselecting, too, because nothing here ever put it back.

     OFF, not "the right cone": these panes ARE the deliverables. The cone
     exists to stop the outside of the recess box showing once the view reaches
     past the screen edge, and in Flat the view is exactly the screen. */
  applyScreenClip(false);
  const desc = rightPanelDesc();
  const ss = S.previewQuality ? clamp(Math.round(S.ss)||1, 1, 4) : 1;
  const pr = Math.min(devicePixelRatio,2) * ss;
  const bg = new THREE.Color(S.bg.color), alpha = S.bg.transparent ? 0 : 1;
  helpers.visible = false;

  const pass = (cam, box) => {
    const w = box.clientWidth, h = box.clientHeight;
    if(!w || !h) return false;
    renderer.setClearColor(bg, alpha);
    applyBillboards(cam);
    updateSelBox();
    updateGizmo(cam);
    updateFaceArrow(cam);
    renderer.setPixelRatio(pr);
    renderer.setSize(w, h, false);
    syncFatLineRes();
    renderComposited(scene, cam);
    if(gizmoLayerVisible()){
      renderer.autoClear = false;
      renderer.clearDepth();
      renderer.render(gizmoScene, cam);
      renderer.autoClear = true;
    }
    return true;
  };

  /* Refused rather than drawn when the viewer is nearly in the second panel's
     plane: the frustum degenerates there and would put NaN through the whole
     matrix. The pane keeps its last good frame instead of flashing black. */
  if(desc && cornerRenderable()){
    updateScreenCam(cornerCam, desc);
    if(pass(cornerCam, $('#fitboxCornerR'))) blitInto('#cvCornerR');
  }
  updateFlatCam();
  pass(flatCam, $('#fitbox'));
  if(mask.active) paintMask();
}

/* Objects must NEVER be drawn outside the frame — only their handles.
   Overscan widens the frustum so an off-frame light still has something to
   grab, but the render itself is the deliverable: the physical screen cannot
   show anything past its own edge, so drawing scene content out there is a lie
   about what will be installed. Scissored to the deliverable rectangle for the
   SCENE passes and left open for the gizmo passes, which is exactly the split
   overscan exists to make.

   The rectangle is the middle 1/m of the drawing buffer — the same inner rect
   blitSite maps onto the marked quad — and it is centred, so the bottom-left
   origin WebGL scissors use needs no special handling. */
function withDeliverableOnly(fn){
  const m = Math.max(1, S.siteOverscan || 1);
  if(!(isSiteLike() && m > 1)){ fn(); return; }
  const w = renderer.domElement.width, h = renderer.domElement.height;
  /* Clear the WHOLE buffer first, unscissored. renderer.render does its own
     clear inside, and a clear obeys the scissor as well — so scissoring the
     pass alone leaves whatever the previous pass wrote outside the rectangle
     sitting there as stale pixels, which is not "no objects outside the
     frame", it is last frame's objects outside the frame. */
  const auto = renderer.autoClear;
  renderer.setScissorTest(false);
  renderer.clear();
  renderer.setScissor(Math.round(w/2 - w/(2*m)), Math.round(h/2 - h/(2*m)),
                      Math.round(w/m), Math.round(h/m));
  renderer.setScissorTest(true);
  renderer.autoClear = false;
  try { fn(); } finally { renderer.setScissorTest(false); renderer.autoClear = auto; }
}

function draw(){
  prepDraw();
  /* SPLIT IS TWO PASSES, and with a photograph the second one is the mockup.
     Rather than a copy of the site path living in drawSplit, the left pane is
     drawn and then this function simply continues INTO that path — every branch
     below already asks isSiteLike(), which is true here, so what follows is the
     same pass the standalone view runs, blitting into the same #siteWrap that
     setView has moved into the pane. Without a photo the right pane is Flat,
     drawSplit draws both, and there is nothing left to do. */
  const siteSplit = S.view === 'split' && !!S.photo;
  if(S.view === 'split' && !siteSplit){ drawSplit(false); return; }
  if(S.view === 'flat' && S.cornerPanel){ drawCorner(); return; }

  const orbit = S.view==='orbit';
  const siteLike = isSiteLike();
  /* On-site is the one pass with the building behind the canvas, so it is
     the one that can show it through the margins. Set per pass, after
     drawSplit has already drawn its own panes with the black bars. */
  setBezelPass(siteLike);
  /* Off for the site pass, and set AFTER drawSplit has already drawn the left
     pane with them on — the orbit helpers belong to that pane, not to the
     photograph. */
  helpers.visible = orbit && S.showHelpers;
  if(orbit){
    // Matches app.css's --stage in each theme — a WebGL clear colour can't
    // read a CSS custom property, so these are kept in sync by hand. Only
    // Orbit: Flat/Site already use S.bg (the export background, a
    // deliberate per-project setting, not workspace chrome) and shouldn't
    // silently change just because the UI theme was toggled.
    renderer.setClearColor(document.documentElement.dataset.theme === 'light' ? STAGE_LIGHT : STAGE_DARK, 1);
    updateOrbit();
  }else{
    /* On-site overscan renders past the screen rect so off-frame objects can be
       seen and grabbed. An opaque clear would fill that whole widened canvas
       with the SCREEN's background and paint the building out of the
       photograph, so it clears transparent instead and blitSite fills the
       background back in over the deliverable rectangle alone, underneath the
       render. Flat and the export pass no margin and are unaffected. */
    const over = siteLike ? autoOverscan() : 1;
    const solid = !S.bg.transparent && over === 1;
    renderer.setClearColor(new THREE.Color(S.bg.color), solid ? 1 : 0);
    /* No zoom, ever. Flat's zoom is a CSS scale of the whole box now
       (ui/flat-view.js), so the camera always renders the tight screen rect
       and the element it lands in is what grows. */
    updateFlatCam(over);
  }
  if(siteLike) sizeRendererForSite();
  applyScreenClip(siteLike && (S.siteOverscan || 1) > 1);
  /* NOT activeCam(). It answers orbitCam for 'split' — correct for the left
     pane, which drawSplit has already finished with — and the mockup is drawn
     through the screen's own frustum like the deliverable it is a picture of. */
  const cam = siteLike ? flatCam : activeCam();
  applyBillboards(cam);
  updateCalibGrid(cam);
  updateSelBox();
  /* The mockup without the working marks, when asked (Scene Settings): the
     selection outline and the null markers are for placing things, and a
     photograph of the site reads better without them. The gizmo stays — it is
     how the selected thing is moved. */
  if(siteLike && S.siteOutlines === false){ selBox.visible = false; setNullMarkers(false); }
  updateGizmo(cam);
  updateFaceArrow(cam);
  syncFatLineRes();
  withDeliverableOnly(() => renderComposited(scene, cam));

  const isSite = siteLike;
  // Grab the clean, gizmo-free scene now — blitSiteGizmo (below) needs
  // siteH, which this sets, and the gizmo pass is about to draw over this
  // same canvas.
  if(isSite){
    syncCamRig();
    blitSite();
    /* The second panel gets its own pass and its own warped layer. Rendered
       AFTER the first is blitted off, since there is one WebGL canvas and this
       overwrites it — the same ordering drawSplit and drawCorner both use. */
    if(S.cornerPanel && cornerRenderable()){
      const d = rightPanelDesc();
      updateScreenCam(cornerCam, d, Math.max(1, S.siteOverscan || 1));
      applyBillboards(cornerCam);
      // this pass sees the SECOND panel's cone, so a recess or frame does not
      // hang past that panel's own edge either
      applyScreenClip((S.siteOverscan || 1) > 1, d);
      withDeliverableOnly(() => renderComposited(scene, cornerCam));
      blitSiteCorner();
      /* Put the camera back, so the gizmo pass below draws through the one the
         pointer is mapped through for whatever is actually selected. Reads
         S.siteOverscan rather than the local `over` from the branch above,
         which is out of scope here — autoOverscan has already written it. */
      updateFlatCam(Math.max(1, S.siteOverscan || 1), 1);
    }else{
      blitSiteCorner();          // hides the layer when there is no second panel
    }
  }

  const showGizmo = gizmoLayerVisible();
  if(showGizmo){
    renderer.autoClear = false;
    renderer.clearDepth();
    /* Drawn through exactly the frustum the pointer is mapped through — see
       the note above the removed widening in git history, and pickGizmo in
       ui/interaction.js, which raycasts through this same camera. */
    if(isSite){
      /* Site composites this as its OWN layer rather than baking it into the
         warped photo — blitSite has already been taken above, and this pass
         draws over the same canvas. Its warp is siteH itself, matching the
         render underneath it exactly. */
      renderer.setClearColor(0x000000, 0);
      renderer.clear(true, true, false);
      renderer.render(gizmoScene, cam);
      /* One expression decides both halves. The second layer is drawn under
         exactly this condition a few lines down, so "panel one keeps its own
         side" and "panel two draws the other side" can never disagree and
         leave a wedge of the photo with no handle on it at all. */
      const twoPanels = !!(S.cornerPanel && cornerRenderable());
      blitSiteGizmo(1, twoPanels);
      /* An object sitting at the corner is visible through BOTH panels, so its
         handles are too — and each half has to be drawn through that panel's
         own frustum. Rendered through the first alone, the gizmo simply stopped
         at the hinge, cut in half. Second pass, second layer. */
      if(twoPanels){
        const gd = rightPanelDesc();
        updateScreenCam(cornerCam, gd, Math.max(1, S.siteOverscan || 1));
        renderer.clear(true, true, false);
        renderer.render(gizmoScene, cornerCam);
        blitSiteGizmoCorner();
        updateFlatCam(Math.max(1, S.siteOverscan || 1), 1);
      }else{
        blitSiteGizmoCorner();      // hides the layer when there is no second panel
      }
    }else{
      renderer.render(gizmoScene, cam);
    }
    renderer.autoClear = true;
  }else if(isSite){
    hideSiteGizmo();
  }

  if(mask.active) paintMask();

  /* AND THE LEFT PANE LAST. #cv is not a picture of the orbit view, it IS the
     orbit view — the live canvas, moved into #splitPaneL — while the mockup is
     a warped COPY of the same canvas taken by blitSite. So the site pass has to
     go first and hand its pixels off, and whatever renders last is what stays
     on screen. Drawn the other way round, the left pane held the site pass's
     leftovers: the deliverable-sized clear and a lone gizmo on black. */
  if(siteSplit) drawSplit(true);
}

function loop(){
  if(consumeRender()) draw();
  requestAnimationFrame(loop);
}
/* Start on the NEXT frame, not synchronously. Calling loop() here would run a
   full draw() in the middle of module evaluation, before the declarations below
   this point exist — and draw() reaches them. With Orbit as the default view
   that stayed hidden, because the code path that touches them (blitSite) only
   runs for the on-site view. Deferring by one frame means the module is fully
   evaluated before anything renders. */
requestAnimationFrame(loop);
